import type { Theme } from '@mui/material';

/**
 * The tone of a modal's header icon badge.
 *
 * Named by meaning rather than by colour so a modal says what it is — a warning,
 * a destructive confirmation, an informational panel — and the palette decides
 * how that reads.
 */
export type ModalIconTone = 'error' | 'warning' | 'info' | 'success' | 'primary' | 'neutral';

export interface ModalIconTint {
  /** Badge fill. */
  bg: string;
  /** Glyph colour. */
  color: string;
}

/**
 * Fill + glyph colour for the circular icon badge every app modal leads with.
 *
 * Shared by `ConfirmationModal` and the remediation dialogs' shell so the two
 * cannot drift — a badge is the first thing a modal shows, and having two
 * definitions of "what warning looks like" is how popups stop looking like the
 * same product.
 *
 * The fills come from the numeric colour scales (`orange[100]`, `blue[100]`, …)
 * rather than from `palette.warning.lighter` and friends, which this theme does
 * not define: MUI derives `light`/`dark` from `main` and never a `lighter`, so
 * every one of those lookups resolved to `undefined` and the badges rendered
 * with no fill at all. The `100` step is the same token the assessment modals
 * already tint their badges with.
 */
export function modalIconTint(theme: Theme, tone: ModalIconTone = 'warning'): ModalIconTint {
  switch (tone) {
    case 'error':
      return { bg: theme.palette.red[100], color: theme.palette.error.main };
    case 'warning':
      return { bg: theme.palette.orange[100], color: theme.palette.warning.main };
    case 'info':
      return { bg: theme.palette.blue[100], color: theme.palette.info.main };
    case 'success':
      return { bg: theme.palette.green[100], color: theme.palette.accent.green };
    case 'primary':
      return { bg: theme.palette.blue[100], color: theme.palette.accent.blue };
    case 'neutral':
      return { bg: theme.palette.grey[200], color: theme.palette.text.secondary };
    default:
      return { bg: theme.palette.orange[100], color: theme.palette.warning.main };
  }
}

/** Diameter of the badge, shared so every modal's header sits at the same height. */
export const MODAL_ICON_BADGE_SIZE = 40;
